/**
 * Scenario chain logic: steps derived from slots, saved answers, and how an answer renders
 * when a later step references it. The backend ports these (webapp/parsing/quiz_scenario_logic.py);
 * tests/fixtures/scenario-vectors.json pins both sides to the same results.
 */

import type { QuizAnswer, QuizQuestion, QuizVariable } from '../types';
import { renderTemplate, VARIABLE_KEY_PATTERN } from './template';

export interface ScenarioStep {
  /** 0-based position in the chain; shown to people as `index + 1`. */
  index: number;
  /** '' when the questions have no slot yet (publish validation rejects that). */
  slotId: string;
  questionIds: string[];
}

/** Consecutive questions with the same slot form one step. A slot that comes back later is a new step. */
export function deriveSteps(questions: Pick<QuizQuestion, 'id' | 'slotId'>[]): ScenarioStep[] {
  const steps: ScenarioStep[] = [];
  for (const q of questions) {
    const slotId = q.slotId ?? '';
    const last = steps[steps.length - 1];
    if (last && last.slotId === slotId) last.questionIds.push(q.id);
    else steps.push({ index: steps.length, slotId, questionIds: [q.id] });
  }
  return steps;
}

export function stepIndexByQuestion(steps: ScenarioStep[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const s of steps) for (const id of s.questionIds) out.set(id, s.index);
  return out;
}

export interface SavedAnswer {
  key: string;
  /** answerLabel, falling back to the key */
  label: string;
  questionId: string;
  /** 0-based question position in the template */
  questionIndex: number;
  stepIndex: number;
}

/** Every question with a "Save answer as" name, in template order. */
export function savedAnswers(questions: QuizQuestion[], steps: ScenarioStep[] = deriveSteps(questions)): SavedAnswer[] {
  const stepOf = stepIndexByQuestion(steps);
  const out: SavedAnswer[] = [];
  questions.forEach((q, questionIndex) => {
    if (!q.answerKey) return;
    out.push({
      key: q.answerKey,
      label: q.answerLabel?.trim() || q.answerKey,
      questionId: q.id,
      questionIndex,
      stepIndex: stepOf.get(q.id) ?? 0,
    });
  });
  return out;
}

/** For each question, the saved answers from strictly earlier steps (what its placeholders may use). */
export function earlierAnswersByQuestion(
  questions: QuizQuestion[],
  steps: ScenarioStep[] = deriveSteps(questions)
): Map<string, SavedAnswer[]> {
  const saved = savedAnswers(questions, steps);
  const out = new Map<string, SavedAnswer[]>();
  for (const step of steps) {
    const earlier = saved.filter((a) => a.stepIndex < step.index);
    for (const id of step.questionIds) out.set(id, earlier);
  }
  return out;
}

/**
 * The text a later step sees for an answer. Text: trimmed. Single / true-false: the choice
 * text. Multi: choice texts in the question's order, joined with ", ". Choice text has its
 * own placeholders filled from `values`. Anything missing or malformed renders as ''
 * (renderTemplate then shows the usual `[key]` marker).
 */
export function renderAnswer(
  question: Pick<QuizQuestion, 'type' | 'choices'>,
  answer: QuizAnswer | null | undefined,
  values: Record<string, string> | undefined
): string {
  if (question.type === 'text') return typeof answer === 'string' ? answer.trim() : '';
  if (!Array.isArray(answer)) return '';
  const picked = new Set(answer);
  const texts = question.choices.filter((c) => picked.has(c.id)).map((c) => renderTemplate(c.text, values));
  return (question.type === 'multi' ? texts : texts.slice(0, 1)).join(', ');
}

/**
 * Saved-answer values each step sees, for the author preview. `contexts[i]` holds the
 * rendered answers of every question in steps before step i; a choice answer is rendered
 * with the values its own step saw.
 */
export function buildChainContexts(
  questions: QuizQuestion[],
  steps: ScenarioStep[],
  answers: Record<string, QuizAnswer>,
  variables: Record<string, string>
): Record<string, string>[] {
  const byId = new Map(questions.map((q) => [q.id, q]));
  const contexts: Record<string, string>[] = [];
  const acc: Record<string, string> = {};
  for (const step of steps) {
    contexts.push({ ...acc });
    const values = { ...variables, ...acc };
    for (const id of step.questionIds) {
      const q = byId.get(id);
      if (q?.answerKey) acc[q.answerKey] = renderAnswer(q, answers[id], values);
    }
  }
  return contexts;
}

/** Inline editor error for a question's "Save answer as" name, or null (blank is allowed). */
export function answerKeyError(
  questionId: string,
  key: string | undefined,
  questions: Pick<QuizQuestion, 'id' | 'answerKey'>[],
  variables: Pick<QuizVariable, 'key'>[]
): string | null {
  if (!key) return null;
  if (!VARIABLE_KEY_PATTERN.test(key)) return 'Start with a letter; letters, digits and _ only (max 32)';
  if (variables.some((v) => v.key === key)) return 'A variable already uses this name';
  const other = questions.findIndex((q) => q.id !== questionId && q.answerKey === key);
  if (other >= 0) return `Question ${other + 1} already saves an answer as this name`;
  return null;
}
